'use memo';

import { dealSourceRelTypeValues, dealStructureTypeValues, type DealItem, type DealSourceRelType, type DealStructureType } from '@standandforce/api-contract';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { Badge } from '@/components/Badge';
import { Checkbox } from '@/components/Checkbox';
import { HStack } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CForm } from '@/features/c-form/CForm';
import { CFormItemBase } from '@/features/c-form/CFormItems/CFormItem';
import { CFormSection } from '@/features/c-form/CFormSection/CFormSection';
import { orpc } from '@/lib/api/orpc';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { DealLabel } from '../-components/DealLabel';
import type { DealOption } from '../-components/DealSelect';
import { DealSelect } from '../-components/DealSelect';
import { useUpdateDealStructure } from '../-mutations/useDealMutations';
import { useGetDeal } from '../-queries/useGetDeal';
const SELECT_NONE_SENTINEL = '__none__';
type StructureDraft = {
  structureTypes: DealStructureType[];
  parentDeal: DealOption | undefined;
  parentChildRelType: string | null;
  sourceDeal: DealOption | undefined;
  sourceRelType: DealSourceRelType | null;
  linkedDeal: DealOption | undefined;
};
type DealLabelResolver = (dealId: string) => string;
function makeDealOption(dealId: string | null, resolveLabel: DealLabelResolver): DealOption | undefined {
  if (!dealId) return undefined;
  return {
    id: dealId,
    label: resolveLabel(dealId)
  };
}
function dealToStructureDraft(deal: DealItem, resolveLabel: DealLabelResolver): StructureDraft {
  return {
    structureTypes: deal.structureTypes,
    parentDeal: makeDealOption(deal.parentDealId, resolveLabel),
    parentChildRelType: deal.parentChildRelType,
    sourceDeal: makeDealOption(deal.sourceDealId, resolveLabel),
    sourceRelType: deal.sourceRelType,
    linkedDeal: makeDealOption(deal.linkedDealId, resolveLabel)
  };
}
function DealStructurePage() {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.dealLedger'
  });
  const {
    dealId
  } = useParams({
    from: '/_staff/deal/$dealId/_layout'
  });
  const {
    data: deal
  } = useGetDeal(dealId);
  const {
    mutate: mutateStructure,
    isPending
  } = useUpdateDealStructure(dealId);
  const queryClient = useQueryClient();

  // 関連 Deal のラベル解決: DealLabel が読み取り表示で useSuspenseQuery により
  // キャッシュ済みのため、編集モード遷移時にキャッシュから dealNumber + title を取得する
  const resolveDealLabel: DealLabelResolver = relatedDealId => {
    const cached = queryClient.getQueryData(orpc.dealLedger.dealGet.queryOptions({
      input: {
        id: relatedDealId
      }
    }).queryKey) as DealItem | undefined;
    return cached ? `${cached.dealNumber} ${cached.title}` : relatedDealId;
  };
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<StructureDraft>(() => dealToStructureDraft(deal, resolveDealLabel));
  function startEdit() {
    setDraft(dealToStructureDraft(deal, resolveDealLabel));
    setIsEditing(true);
  }
  function cancelEdit() {
    setIsEditing(false);
  }
  function saveEdit() {
    mutateStructure({
      structureTypes: draft.structureTypes,
      parentDealId: draft.parentDeal?.id ?? null,
      parentChildRelType: draft.parentChildRelType,
      sourceDealId: draft.sourceDeal?.id ?? null,
      sourceRelType: draft.sourceRelType,
      linkedDealId: draft.linkedDeal?.id ?? null
    }, {
      onSuccess: () => {
        setIsEditing(false);
      }
    });
  }
  function toggleStructureType(type: DealStructureType, checked: boolean) {
    setDraft(prev => {
      const newTypes = checked ? [...prev.structureTypes, type] : prev.structureTypes.filter(st => st !== type);
      if (!checked) {
        if (type === 'PARENT_CHILD') {
          return {
            ...prev,
            structureTypes: newTypes,
            parentDeal: undefined,
            parentChildRelType: null
          };
        }
        if (type === 'SOURCE_DEAL') {
          return {
            ...prev,
            structureTypes: newTypes,
            sourceDeal: undefined,
            sourceRelType: null
          };
        }
        if (type === 'BACK_TO_BACK') {
          return {
            ...prev,
            structureTypes: newTypes,
            linkedDeal: undefined
          };
        }
      }
      return {
        ...prev,
        structureTypes: newTypes
      };
    });
  }
  const activeTypes = isEditing ? draft.structureTypes : deal.structureTypes;
  const showParentChild = activeTypes.includes('PARENT_CHILD');
  const showSourceDeal = activeTypes.includes('SOURCE_DEAL');
  const showBackToBack = activeTypes.includes('BACK_TO_BACK');

  // Permission により非表示の属性は deal.structureSections に含まれない
  const visibleAttributeKeys = new Set(deal.structureSections.flatMap(s => s.attributes.map(a => a.attributeKey)));
  const isVisible = (attributeKey: string) => visibleAttributeKeys.has(attributeKey);

  // 描画順に各セクションの表示条件を並べ、最初に表示されるセクションへ
  // actionButton（編集開始/保存/キャンセル）を渡す
  const sectionVisibility = [isVisible('DealStructureType'), showParentChild && isVisible('DealParentID'), showSourceDeal && (isVisible('DealSourceID') || isVisible('DealSourceRelationType')), showBackToBack && isVisible('DealLinkedDealID')];
  const firstVisibleSectionIndex = sectionVisibility.findIndex(Boolean);
  const actionButton = !isEditing ? <Button variant="outline" size="sm" onClick={startEdit}>
      {t('flowTabButtonEdit')}
    </Button> : <HStack className="gap-2">
      <Button variant="outline" size="sm" onClick={cancelEdit} disabled={isPending}>
        {t('flowTabButtonCancel')}
      </Button>
      <Button size="sm" onClick={saveEdit} disabled={isPending}>
        {t('flowTabButtonSave')}
      </Button>
    </HStack>;
  return <CForm editUnit="section" itemLayout="card">
      {isVisible('DealStructureType') && <CFormSection id="deal-structure-types" label={t('structureTabTitle')} hideActionButtons actionButton={firstVisibleSectionIndex === 0 ? actionButton : undefined}>
          <CFormItemBase id="structureTypes" label={t('structureFieldStructureTypes')} isEditing={isEditing} hideActionButtons readComponent={deal.structureTypes.length > 0 ? <HStack className="gap-2 flex-wrap">
                  {deal.structureTypes.map(type => <Badge key={type} variant="neutralMuted" className="text-xs">
                      {t(`dealStructureType.${type}`)}
                    </Badge>)}
                </HStack> : ''} writeComponent={<HStack className="gap-4 flex-wrap">
                {dealStructureTypeValues.map(type => <Checkbox key={type} label={t(`dealStructureType.${type}`)} checked={draft.structureTypes.includes(type)} onCheckedChange={checked => toggleStructureType(type, checked)} />)}
              </HStack>} />
        </CFormSection>}

      {showParentChild && isVisible('DealParentID') && <CFormSection id="deal-structure-parent-child" label={t('structureSectionParentChild')} hideActionButtons actionButton={firstVisibleSectionIndex === 1 ? actionButton : undefined}>
          <CFormItemBase id="parentDealId" label={t('structureFieldParentDealId')} isEditing={isEditing} hideActionButtons readComponent={<DealLabel dealId={deal.parentDealId} />} writeComponent={<DealSelect value={draft.parentDeal} onChange={option => setDraft(prev => ({
        ...prev,
        parentDeal: option,
        parentChildRelType: option ? prev.parentChildRelType : null
      }))} allowEmpty placeholder={t('structureFieldDealSelectPlaceholder')} />} />
        </CFormSection>}

      {showSourceDeal && (isVisible('DealSourceID') || isVisible('DealSourceRelationType')) && <CFormSection id="deal-structure-source-deal" label={t('structureSectionSourceDeal')} hideActionButtons actionButton={firstVisibleSectionIndex === 2 ? actionButton : undefined}>
            {isVisible('DealSourceID') && <CFormItemBase id="sourceDealId" label={t('structureFieldSourceDealId')} isEditing={isEditing} hideActionButtons readComponent={<DealLabel dealId={deal.sourceDealId} />} writeComponent={<DealSelect value={draft.sourceDeal} onChange={option => setDraft(prev => ({
        ...prev,
        sourceDeal: option,
        sourceRelType: option ? prev.sourceRelType : null
      }))} allowEmpty placeholder={t('structureFieldDealSelectPlaceholder')} />} />}
            {isVisible('DealSourceRelationType') && <CFormItemBase id="sourceRelType" label={t('structureFieldSourceRelType')} isEditing={isEditing} hideActionButtons readComponent={deal.sourceRelType ? t(`dealSourceRelType.${deal.sourceRelType}`) : ''} writeComponent={<Select value={draft.sourceRelType ?? SELECT_NONE_SENTINEL} onValueChange={v => setDraft(prev => ({
        ...prev,
        sourceRelType: v === SELECT_NONE_SENTINEL ? null : v as DealSourceRelType
      }))}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={SELECT_NONE_SENTINEL}>
                        &mdash;
                      </SelectItem>
                      {dealSourceRelTypeValues.map(v => <SelectItem key={v} value={v}>
                          {t(`dealSourceRelType.${v}`)}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>} />}
          </CFormSection>}

      {showBackToBack && isVisible('DealLinkedDealID') && <CFormSection id="deal-structure-back-to-back" label={t('structureSectionBackToBack')} hideActionButtons actionButton={firstVisibleSectionIndex === 3 ? actionButton : undefined}>
          <CFormItemBase id="linkedDealId" label={t('structureFieldLinkedDealId')} isEditing={isEditing} hideActionButtons readComponent={<DealLabel dealId={deal.linkedDealId} />} writeComponent={<DealSelect value={draft.linkedDeal} onChange={option => setDraft(prev => ({
        ...prev,
        linkedDeal: option
      }))} allowEmpty placeholder={t('structureFieldDealSelectPlaceholder')} />} />
        </CFormSection>}
    </CForm>;
}
export const Route = createFileRoute('/_staff/deal/$dealId/_layout/structure')({
  component: DealStructurePage
});